import { Box, Link, TextField, Typography, useTheme } from "@material-ui/core";
import ibanLib from "iban";
import { InformationMessage, ThemedAsset, assetIds } from "nemuru-components";
import React, { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { ICON_TITLE_HEIGHT } from "../../../../../config/config";
import IconTitle from "../../../../shared/components/IconTitle";
import InfoPanel from "../../../../shared/components/InfoPanel";
import MainButton from "../../../../shared/components/MainButton";
import Text from "../../../../shared/components/Text";
import { infoDataSEPAExplanation } from "../../../kyc/shared/config";
import { AlternativePaymentMethodFactory } from "../../DeclaredIncome/shared/components/AlternativePaymentMethodFactory/AlternativePaymentMethodFactory";
import { useIBAN } from "../hooks/useIBAN";
import { getInformationTextByNonValidAction } from "../utils/getInformationTextByNonValidAction";
import { ProcessContext } from "../../../../../contexts/ProcessContext";
import { AssetType } from "@nemuru-eng/js-api-client";

export default function SetBankAccount({
  onSubmitForm,
  data,
  recordedIBAN,
  forceCaixaBankIBAN,
  allowInternationalBorrower,
  alternativePaymentMethod,
}) {
  const { palette } = useTheme();
  const {
    state: { loan },
  } = useContext(ProcessContext);

  const { register, watch, getValues, handleSubmit, errors } = useForm({
    defaultValues: {
      bankAccountIBAN:
        recordedIBAN !== null ? recordedIBAN : data?.bankAccountIBAN,
    },
  });

  const [showAlternativePaymentDialog, setShowAlternativePaymentDialog] =
    useState(false);

  const IBAN = watch("bankAccountIBAN");

  const {
    checkIBANValidation,
    validateIfIBANIsSpanish,
    validateIfIBANIsCaixabank,
    notValidIBANReason,
  } = useIBAN({
    allowInternationalBorrower,
    forceCaixaBankIBAN,
  });

  return (
    <>
      <Box className="appear-anim">
        <IconTitle
          icon={
            <ThemedAsset
              id={assetIds.ICON_BANK}
              width={ICON_TITLE_HEIGHT}
              height={ICON_TITLE_HEIGHT}
              style={{ color: palette.secondary.main }}
            />
          }
          title={<Text tId="FINANCIAL_DATA" />}
          subtitle={<Text tId="DIRECT_DEBIT_ACCOUNT" />}
        />
        {forceCaixaBankIBAN && (
          <Box my={2}>
            <Typography variant={"body2"}>
              <Text tId={"CAIXABANK_IBAN_ONLY"} />
            </Typography>
          </Box>
        )}
        <TextField
          fullWidth
          value={IBAN ? ibanLib.printFormat(IBAN) : ""}
          label={
            allowInternationalBorrower ? (
              <Text tId={"BANK_ACCOUNT_IBAN_WITHIN_SEPA"} />
            ) : (
              <Text tId={"BANK_ACCOUNT_IBAN"} />
            )
          }
          type="text"
          id="bankAccountIBAN"
          name="bankAccountIBAN"
          error={errors.bankAccountIBAN ? true : false}
          helperText={errors.bankAccountIBAN?.message}
          inputProps={{
            ref: register({
              required: {
                value: true,
                message: <Text tId={"REQUIRED_FIELD"} />,
              },
              validate: {
                isIBANValid: (value) => checkIBANValidation(value),
                isIBANSpanish: (value) => validateIfIBANIsSpanish(value),
                isIBANCaixabank: (value) => validateIfIBANIsCaixabank(value),
              },
            }),
          }}
        />
        {allowInternationalBorrower && alternativePaymentMethod && (
          <Box mt={1}>
            <Typography color={"textPrimary"} variant={"caption"}>
              <Link
                onClick={() => setShowAlternativePaymentDialog(true)}
                style={{ color: palette.text.secondary, cursor: "pointer" }}
              >
                <Text
                  tId={"DO_NOT_HAVE_BANK_ACCOUNT_NUMBER_WITHIN_THE_SEPA_ZONE"}
                />
              </Link>
            </Typography>
          </Box>
        )}
      </Box>
      <Box>
        <Box className="appear-anim">
          <InformationMessage
            variant={{
              type: notValidIBANReason ? "warning" : "info",
              icon: (
                <i className="material-icons-outlined" style={{ fontSize: 20 }}>
                  error_outline
                </i>
              ),
            }}
            isScreenXs={true}
            messageContent={{
              body: notValidIBANReason ? (
                getInformationTextByNonValidAction(notValidIBANReason)
              ) : allowInternationalBorrower ? (
                <Text
                  tId={"BANK_ACCOUNT_USAGE_REMINDER_FOR_INTERNATIONAL_STUDENT"}
                />
              ) : loan.asset_reference[0].type === AssetType.CARD ? (
                <Text tId="BANK_ACCOUNT_USAGE_REMINDER_FOR_CARD" />
              ) : (
                <Text tId="BANK_ACCOUNT_USAGE_REMINDER" />
              ),
            }}
          />
        </Box>

        <MainButton
          disabled={notValidIBANReason}
          onClick={handleSubmit(() => onSubmitForm(getValues()))}
        />
      </Box>
      {showAlternativePaymentDialog && alternativePaymentMethod && (
        <AlternativePaymentMethodFactory
          alternativePaymentMethod={alternativePaymentMethod}
          onSubmitForm={onSubmitForm}
          showAlternativePaymentDialog={showAlternativePaymentDialog}
          setShowAlternativePaymentDialog={setShowAlternativePaymentDialog}
        />
      )}
      {allowInternationalBorrower && (
        <InfoPanel infoData={infoDataSEPAExplanation} />
      )}
    </>
  );
}
